import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  { value: "buying", label: "Buying a home" },
  { value: "selling", label: "Selling a home" },
  { value: "mortgage", label: "Mortgage / Refinance" },
  { value: "general", label: "General question" },
];

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Message sent — J.J. will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", interest: "general", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please call or email instead.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-bone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Left — info */}
        <div className="md:col-span-5">
          <p className="overline text-ink">Get in touch</p>
          <h2 className="mt-5 font-display text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Let's talk
            <br />
            <span className="italic text-slate2">about your move.</span>
          </h2>
          <p className="mt-6 text-text2/80 text-base md:text-lg leading-relaxed">
            Whether you're a few months out or ready to write an offer this weekend — I'd
            love to hear what you're working on.
          </p>

          <ul className="mt-12 space-y-6">
            <ContactRow
              icon={Phone}
              label="Phone"
              value={SITE.phone}
              href={SITE.phoneHref}
              testid="contact-phone"
            />
            <ContactRow
              icon={Mail}
              label="Real Estate"
              value={SITE.emails.realty}
              href={`mailto:${SITE.emails.realty}`}
              testid="contact-email-realty"
            />
            <ContactRow
              icon={Mail}
              label="Mortgage"
              value={SITE.emails.mortgage}
              href={`mailto:${SITE.emails.mortgage}`}
              testid="contact-email-mortgage"
            />
            <ContactRow
              icon={MapPin}
              label="Service Area"
              value="Okaloosa, Walton & Santa Rosa Counties · Mortgage in all 50 states"
              testid="contact-region"
            />
          </ul>

          <a
            href={SITE.mortgageApplyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-side-apply-btn"
            className="mt-12 inline-flex items-center gap-2 group text-ink text-sm font-medium border-b border-ink pb-1 transition-all hover:border-brass hover:text-brass"
          >
            Start your mortgage application
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {/* Right — form */}
        <div className="md:col-span-7">
          <div className="bg-white border border-border2 rounded-2xl p-8 md:p-10 shadow-sm">
            {done ? (
              <div
                data-testid="contact-success"
                className="text-center py-12"
              >
                <CheckCircle2 className="w-14 h-14 mx-auto text-brass" />
                <h3 className="mt-6 font-display text-ink text-3xl">Message received.</h3>
                <p className="mt-3 text-text2/80 max-w-sm mx-auto">
                  Thanks for reaching out. J.J. will reply within one business day —
                  usually much sooner.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="contact-send-another"
                  className="mt-8 text-sm text-ink border-b border-ink pb-1 hover:text-brass hover:border-brass"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Full name" htmlFor="name">
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={setField("name")}
                      placeholder="Jane Doe"
                      required
                      className="bg-bone border-border2 focus-visible:ring-ink"
                    />
                  </Field>
                  <Field label="Email" htmlFor="email">
                    <Input
                      id="email"
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={setField("email")}
                      placeholder="you@email.com"
                      required
                      className="bg-bone border-border2 focus-visible:ring-ink"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Phone (optional)" htmlFor="phone">
                    <Input
                      id="phone"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={setField("phone")}
                      placeholder="850.000.0000"
                      className="bg-bone border-border2 focus-visible:ring-ink"
                    />
                  </Field>
                  <Field label="I'm interested in" htmlFor="interest">
                    <Select
                      value={form.interest}
                      onValueChange={(v) => setForm((p) => ({ ...p, interest: v }))}
                    >
                      <SelectTrigger
                        id="interest"
                        data-testid="contact-input-interest"
                        className="bg-bone border-border2 focus:ring-ink"
                      >
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {INTEREST_OPTIONS.map((o) => (
                          <SelectItem
                            key={o.value}
                            value={o.value}
                            data-testid={`contact-interest-${o.value}`}
                          >
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="How can J.J. help?" htmlFor="message">
                  <Textarea
                    id="message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={setField("message")}
                    placeholder="Tell me a little about your timeline, area of interest, or current situation…"
                    rows={5}
                    required
                    className="bg-bone border-border2 focus-visible:ring-ink resize-none"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-ink text-white py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-slate2 transition-colors duration-200 disabled:opacity-60"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? "Sending…" : "Send message"}
                </button>

                <p className="text-xs text-muted2 text-center pt-2">
                  By submitting, you agree to be contacted by J.J. regarding your inquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <Label
        htmlFor={htmlFor}
        className="text-xs uppercase tracking-widest text-muted2 mb-2 inline-block"
      >
        {label}
      </Label>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <>
      <span className="flex-shrink-0 w-10 h-10 rounded-full border border-border2 inline-flex items-center justify-center">
        <Icon className="w-4 h-4 text-ink" />
      </span>
      <div>
        <div className="overline text-muted2">{label}</div>
        <div className="text-ink text-base mt-1">{value}</div>
      </div>
    </>
  );
  return (
    <li>
      {href ? (
        <a
          href={href}
          data-testid={testid}
          className="flex items-start gap-4 group hover:opacity-80 transition-opacity"
        >
          {inner}
        </a>
      ) : (
        <div data-testid={testid} className="flex items-start gap-4">
          {inner}
        </div>
      )}
    </li>
  );
}
